import { GoogleGenerativeAI } from "@google/generative-ai";
import { Dialog } from "@headlessui/react";
import CryptoJS from "crypto-js";
import {
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	updateDoc,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { db } from "../firebaseConfig";

const Dashboard = () => {
	const { user, encryptionKey } = useUser();
	const [topics, setTopics] = useState([]);
	const [markedDates, setMarkedDates] = useState([]);
	const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
	const [isLoadingTopics, setIsLoadingTopics] = useState(true);
	const [runTour, setRunTour] = useState(false);
	const [showEmailPrefs, setShowEmailPrefs] = useState(false);
	const [selectedPrefs, setSelectedPrefs] = useState([
		"progress",
		"prompts",
		"educational",
	]);
	const [bookmarkedSuggestions, setBookmarkedSuggestions] = useState([]);
	const navigate = useNavigate();

	const DashboardSteps = [
		{
			target: ".dashboard-section",
			content:
				"This is your handy dashboard. This will greet you every time you log in.",
			placement: "center",
		},
		{
			target: ".calendar-section",
			content:
				"View your activity history. Blue dots indicate days with sessions. Let's build a streak!",
			placement: "left",
		},
		{
			target: ".topics-section",
			content:
				"Get personalized topics to discuss based on your sessions",
			placement: "top",
		},
		{
			target: ".dashboard-section",
			content:
				"Before you ask: All your data is securely end to end encrypted. It's only visible to you!",
			placement: "center",
		},
	];

	useEffect(() => {
		const fetchData = async () => {
			if (!user || !encryptionKey) return;

			// Check dashboard demo status
			const userRef = doc(db, "Users", user.uid);
			const userDoc = await getDoc(userRef);
			if (userDoc.exists()) {
				const userData = userDoc.data();
				if (!userData.dashboardDemo) {
					setRunTour(true);
					await updateDoc(userRef, { dashboardDemo: true });
				} else if (!userData.emailPrefsSet) {
					setShowEmailPrefs(true);
				}
			}

			const sessionsCollection = collection(
				db,
				"Users",
				user.uid,
				"sessions"
			);

			const sessionsSnapshot = await getDocs(sessionsCollection);

			// Combine and process all activities
			const activities = sessionsSnapshot.docs.map((doc) => ({
				...doc.data(),
				date: doc.data().createdAt?.toDate(),
				type: "therapy",
			}));

			if (activities.length > 0) {
				// Set marked dates for the calendar
				const activityDates = activities
					.filter((activity) => activity.date)
					.map((activity) => activity.date.toDateString());
				setMarkedDates([...new Set(activityDates)]);
			}

			let conversationHistory = "";
			sessionsSnapshot.docs.forEach((doc) => {
				const data = doc.data();
				const messageKeys = Object.keys(data)
					.filter((key) => key.startsWith("message_"))
					.sort();

				messageKeys.forEach((key) => {
					const decryptedMessage = CryptoJS.AES.decrypt(
						data[key],
						encryptionKey
					).toString(CryptoJS.enc.Utf8);
					conversationHistory += `${decryptedMessage}\n`;
				});
			});

			// Set loading to true before generating topics
			setIsLoadingTopics(true);

			if (conversationHistory.length === 0) {
				setTopics([
					"How are you feeling today?",
					"What is occupying your mind recently?",
					"What are your short term goals?",
				]);
				setIsLoadingTopics(false);
				return;
			}

			// Generate topics using Gemini
			try {
				const model = genAI.getGenerativeModel({
					model: "gemini-1.5-flash",
				});
				const prompt = `Based on these therapy conversations, suggest 3 specific, thoughtful questions for the user to explore further. Make them personal and relevant to their experiences, but extremely concise:

				Therapy conversations: ${conversationHistory}

				Format the response as a semicolon delimited string with 3 questions, no headers. Ensure that the questions are very short and don't contain any special characters. Ensure that semicolons are not used in the questions but are used as delimiters.`;

				const result = await model.generateContent(prompt);
				const response = result.response.text();
				// Parse CSV string into array
				const newTopics = response
					.split(";")
					.map((topic) => topic.trim());
				setTopics(newTopics);
			} catch (error) {
				console.error("Error generating topics:", error);
				setIsLoadingTopics(false);
				setTopics([
					"How are you feeling today?",
					"What is occupying your mind recently?",
					"What are your short term goals?",
				]);
			} finally {
				setIsLoadingTopics(false);
			}
		};

		fetchData();
	}, [user, encryptionKey]);

	useEffect(() => {
		const fetchBookmarks = async () => {
			if (!user || !encryptionKey) return;

			try {
				const bookmarksRef = collection(
					db,
					"Users",
					user.uid,
					"bookmarks"
				);
				const bookmarksSnapshot = await getDocs(bookmarksRef);
				const bookmarks = [];

				bookmarksSnapshot.forEach((doc) => {
					const decryptedSuggestion = CryptoJS.AES.decrypt(
						doc.data().suggestion,
						encryptionKey
					).toString(CryptoJS.enc.Utf8);
					bookmarks.push(decryptedSuggestion);
				});

				setBookmarkedSuggestions(bookmarks);
			} catch (error) {
				console.error("Error fetching bookmarks:", error);
			}
		};

		fetchBookmarks();
	}, [user, encryptionKey]);

	// Add these calendar helper functions
	const getDaysInMonth = (date) => {
		return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
	};

	const getFirstDayOfMonth = (date) => {
		return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
	};

	// Custom calendar component
	const CustomCalendar = () => {
		const currentDate = new Date();
		const daysInMonth = getDaysInMonth(currentDate);
		const firstDayOfMonth = getFirstDayOfMonth(currentDate);
		const monthName = currentDate.toLocaleString("default", {
			month: "long",
		});
		const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

		return (
			<div className="w-full max-w-md mx-auto">
				<div className="text-lg text-center font-light mb-3">
					{monthName} Activity
				</div>
				<div className="grid grid-cols-7 gap-3">
					{/* Week days - Updated className to match calendar days width */}
					{weekDays.map((day) => (
						<div
							key={day}
							className="w-7 text-center text-base text-gray-500 font-light">
							{day[0]}
						</div>
					))}

					{/* Empty cells for days before the first day of month */}
					{Array(firstDayOfMonth)
						.fill(null)
						.map((_, index) => (
							<div key={`empty-${index}`} className="h-6" />
						))}

					{/* Calendar days */}
					{Array(daysInMonth)
						.fill(null)
						.map((_, index) => {
							const day = index + 1;
							const dateStr = new Date(
								currentDate.getFullYear(),
								currentDate.getMonth(),
								day
							).toDateString();
							const isMarked = markedDates.includes(dateStr);

							return (
								<div
									key={day}
									className={`w-6 h-6 flex items-center justify-center rounded-full
									${isMarked ? "bg-blue-500 text-white" : "text-gray-700"}`}>
									{day}
								</div>
							);
						})}
				</div>
			</div>
		);
	};

	const handleEmailPreferences = async () => {
		const userRef = doc(db, "Users", user.uid);
		await updateDoc(userRef, {
			emailPreferences: selectedPrefs,
			emailPrefsSet: true,
		});
		setShowEmailPrefs(false);
	};

	const handleDeleteBookmark = async (suggestionToDelete, index) => {
		if (!user || !encryptionKey) return;

		try {
			const bookmarksRef = collection(db, "Users", user.uid, "bookmarks");
			const bookmarksSnapshot = await getDocs(bookmarksRef);

			// Find and delete the matching bookmark
			for (const doc of bookmarksSnapshot.docs) {
				const decryptedSuggestion = CryptoJS.AES.decrypt(
					doc.data().suggestion,
					encryptionKey
				).toString(CryptoJS.enc.Utf8);

				if (decryptedSuggestion === suggestionToDelete) {
					await deleteDoc(doc.ref);
					// Update local state
					setBookmarkedSuggestions((prevBookmarks) =>
						prevBookmarks.filter((_, i) => i !== index)
					);
					break;
				}
			}
		} catch (error) {
			console.error("Error deleting bookmark:", error);
		}
	};

	const handleTopicClick = (topic) => {
		navigate("/therapy", {
			state: {
				initialMessage: `Hi, today I want to talk about the topic "${topic.toLowerCase()}"`,
			},
		});
	};

	return (
		<>
			<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 dashboard-section">
				<div className="w-full max-w-6xl backdrop-blur-lg rounded-3xl shadow-xl overflow-auto bg-white bg-opacity-90">
					<div className="p-6 flex flex-col h-full">
						<div className="flex justify-center items-center mb-3 py-3">
							<h1 className="text-3xl font-light text-gray-800">
								Dashboard
							</h1>
						</div>

						{/* Stats and Calendar Row */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
							{/* Topics Section - Moved here to replace Statistics */}
							<div className="topics-section bg-white rounded-xl shadow-sm border border-gray-100 p-4">
								<h2 className="text-xl font-light text-gray-800 mb-4 text-center">
									New Topics to Explore
								</h2>
								{isLoadingTopics ? (
									<div className="flex justify-center items-center h-32">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
									</div>
								) : (
									<div className="flex flex-col justify-center h-[calc(100%-2rem)] space-y-3 max-h-[25vh] overflow-y-auto pr-2">
										{topics.map((topic, index) => (
											<div
												key={index}
												onClick={() =>
													handleTopicClick(topic)
												}
												className="p-3 bg-blue-500 text-sm text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-all duration-300">
												{topic}
											</div>
										))}
									</div>
								)}
							</div>

							{/* Calendar Section */}
							<div className="calendar-section bg-white rounded-xl shadow-sm border border-gray-100 p-4">
								<CustomCalendar />
							</div>
						</div>

						{/* Topics and Bookmarks Section - Remove Topics section from here */}
						<div className="mt-auto flex justify-center">
							{/* Bookmarks Section - Now spans full width */}
							<div className="bookmarks-section w-full min-h-[200px] bg-white p-4 rounded-xl shadow-sm border border-gray-100">
								<h2 className="text-xl font-light text-gray-800 mb-4 text-center">
									Saved Bookmarks
								</h2>
								<div className="space-y-2 max-h-[25vh] overflow-y-auto pr-2">
									{bookmarkedSuggestions.length === 0 ? (
										<div className="text-center text-gray-500 italic">
											No bookmarks yet. All your saved
											suggestions from your sessions will
											pop up here!
										</div>
									) : (
										bookmarkedSuggestions.map(
											(suggestion, index) => (
												<div
													key={index}
													className="p-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-all duration-300 relative group">
													{suggestion}
													<button
														onClick={() =>
															handleDeleteBookmark(
																suggestion,
																index
															)
														}
														className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:text-red-600"
														aria-label="Delete bookmark">
														<svg
															xmlns="http://www.w3.org/2000/svg"
															className="h-4 w-4"
															fill="none"
															viewBox="0 0 24 24"
															stroke="currentColor">
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M6 18L18 6M6 6l12 12"
															/>
														</svg>
													</button>
												</div>
											)
										)
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Joyride
				steps={DashboardSteps}
				run={runTour}
				continuous={true}
				showSkipButton={true}
				showProgress={false}
				styles={{
					options: {
						primaryColor: "#60a5fa",
						textColor: "#374151",
					},
					buttonClose: {
						display: "none",
					},
				}}
				locale={{
					last: "Done!",
				}}
			/>

			{/* Add Email Preferences Dialog */}
			<Dialog
				open={showEmailPrefs}
				onClose={() => {}}
				className="relative z-50">
				<div className="fixed inset-0 bg-black/30" aria-hidden="true" />

				<div className="fixed inset-0 flex items-center justify-center p-4">
					<Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-4">
						<Dialog.Title className="text-xl font-medium mb-4">
							Choose Your Email Preferences. You can always change
							them in settings.
						</Dialog.Title>
						<div className="space-y-4">
							{[
								{
									id: "progress",
									title: "Progress Updates",
									description:
										"Get personalized summaries of your milestones and reminders to continue your growth.",
								},
								{
									id: "prompts",
									title: "Reflection Prompts",
									description:
										"Inspiration your personal growth, tailored just for you.",
								},
								{
									id: "educational",
									title: "Educational Content",
									description:
										"Receive expert tips, wellness strategies, and curated mental health resources.",
								},
							].map((pref) => (
								<div
									key={pref.id}
									className="flex items-start space-x-3">
									<input
										type="checkbox"
										id={pref.id}
										checked={selectedPrefs.includes(
											pref.id
										)}
										onChange={(e) => {
											if (e.target.checked) {
												setSelectedPrefs([
													...selectedPrefs,
													pref.id,
												]);
											} else {
												setSelectedPrefs(
													selectedPrefs.filter(
														(p) => p !== pref.id
													)
												);
											}
										}}
										className="mt-1"
									/>
									<label
										htmlFor={pref.id}
										className="flex flex-col">
										<span className="font-medium">
											{pref.title}
										</span>
										<span className="text-sm text-gray-500">
											{pref.description}
										</span>
									</label>
								</div>
							))}

							<div className="pt-4 flex justify-end space-x-3">
								<button
									onClick={() => {
										handleEmailPreferences();
									}}
									className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-700">
									{selectedPrefs.length > 0
										? "Save Preferences"
										: "No, thank you"}
								</button>
							</div>
						</div>
					</Dialog.Panel>
				</div>
			</Dialog>
		</>
	);
};

export default Dashboard;
